<template>
  <v-popover
    interactive
    tag="div"
    content-tag="div"
    placement="bottom-end"
    :role="'poper'"
    :trigger="'click'"
    class="ml-2 relative h-8"
    :append-to="bodyRef"
    :on-show="() => !layoutButtonDisabled"
  >
    <template #default="{ state }">
      <div
        v-if="showBlueDot"
        class="w-2 h-2 bg-blue-500 absolute rounded-full top-3 -left-2 pointer-events-none"
      ></div>
      <div
        class="btn-gray group h-8 usn bg-background-strongest text-text-default hover:bg-icon-bg"
        :class="{
          'bg-icon-bg': state.isVisible,
          'w-28': !isMobile,
          'text-inactive-text pointer-events-none': layoutButtonDisabled,
        }"
      >
        <em
          :style="[getCustomIconStyle(layoutDropdownIcon)]"
          class="custom-icons dropdown-item-icon bg-icon-default"
          :class="{ 'mr-1.5 h-5 w-5': isMobile, 'bg-inactive-icon': layoutButtonDisabled }"
        />
        <span
          v-if="!isMobile"
          :class="{ 'text-inactive-text': layoutButtonDisabled }"
        >
          {{ translate('layout') }}
        </span>

        <em
          :style="getCustomIconStyle(iconPath('chevron-down'))"
          class="custom-icons ml-auto h-4 w-4 text-xs bg-icon-default group-hover:bg-icon-hover"
          :class="{
            'bg-icon-hover': state.isVisible,
            'bg-inactive-icon': layoutButtonDisabled,
          }"
        />
      </div>
    </template>
    <template #content="{ hide, state }">
      <ListDropdown
        v-if="state.isVisible"
        class="layout-dropdown w-64"
        :items="dropdownItems"
        :title="isViewOrMyWork || isSprint ? '' : translate('layout')"
        @closeEscape="hide"
        @click="onDropdownItemClick($event, hide)"
      />
    </template>
  </v-popover>
</template>

<script>
import customIconsMixin from '@/mixins/customIconsMixin';
import ListDropdown from '@/components/widgets/ListDropdown';
import BookmarkMixin from '@/mixins/BookmarkMixin';
import { groupByOption } from '@/types/groupByOption';
import { browserStorageGetAndSetItem } from '@/utilities/browser-storage.util';
import { localBoardLayoutKey } from '@/utilities/localDrafts';
import viewFilterMixin from '@/mixins/viewFiltersMixin';
import viewRouteCheckMixin from '@/mixins/viewRouteCheckMixin';
import hideDropdownsMixin from '@/mixins/hideDropdownsMixin';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { BoardLayout } from '@superthread-com/api';
import useTimelineLayoutFeature from '@/utilities/composables/useTimelineLayoutFeature';
import useCalendarLayoutFeature from '@/utilities/composables/useCalendarLayoutFeature';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'CardsLayoutOptionsButton',
  components: {
    ListDropdown,
  },

  mixins: [
    customIconsMixin,
    BookmarkMixin,
    viewFilterMixin,
    viewRouteCheckMixin,
    hideDropdownsMixin,
  ],

  emits: ['updateAttribute'],

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { allowSwitchingToTimelineLayout, showTimelineUpgradeBadge } = useTimelineLayoutFeature();
    const { allowSwitchingToCalendarLayout, showCalendarUpgradeBadge } = useCalendarLayoutFeature();
    const { isGuestReader } = useCurrentRole();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      showTimelineUpgradeBadge,
      allowSwitchingToTimelineLayout,
      allowSwitchingToCalendarLayout,
      showCalendarUpgradeBadge,
      bodyRef: document.body,
      isGuestReader,
    };
  },

  computed: {
    layoutButtonDisabled() {
      return (
        (this.$route.name === this.$constants.routeNames.newBoard ||
          this.currentBoard?.archived ||
          this.$route.name === this.$constants.routeNames.inbox) &&
        !this.inEpicBoard
      );
    },
    defaultSortByOpt() {
      return this.inEpicBoard ? 'time_created' : 'manual';
    },

    defaultGroupByOpt() {
      return this.inEpicBoard ? groupByOption.statusType : groupByOption.status;
    },

    currentBoard() {
      return this.$store.getters.getFlatBoards[this.currentBoardId] || {};
    },

    cardsLayout() {
      return this.$store.getters.cardsLayoutOptions;
    },

    currentBoardId() {
      return this.embed ? this.embeddedBoardAttributes.boardId : this.$store.getters.currentBoardId;
    },

    filteredGroupByOptions() {
      const optionsToShow = {
        [groupByOption.none]: this.currentCardsLayout !== BoardLayout.Board,
        [groupByOption.statusType]: this.inEpicBoard,
        [groupByOption.space]: this.inEpicBoard,
        [groupByOption.status]: !this.inEpicBoard,
        [groupByOption.epic]: !this.inEpicBoard && !this.isEpicsBoard && !this.isGuestReader,
      };

      return this.$store.getters.boardGroupByOptions.filter((opt) => {
        return optionsToShow[opt.value] !== undefined ? optionsToShow[opt.value] : true;
      });
    },

    filteredBoardSortByOptions() {
      const showManualOpt = !this.isViewOrMyWork && !this.inEpicBoard;
      return this.$store.getters.boardSortByOptions.filter((opt) =>
        opt.value === 'manual' ? showManualOpt : true
      );
    },

    dropdownItems() {
      const layouts = this.cardsLayout.map(({ value: id, label, icon }, index) => {
        const showUpgradeBadge =
          this.showTimelineUpgradeBadge(id) || this.showCalendarUpgradeBadge(id);
        return {
          id,
          label: this.translate(label),
          customIconName: this.iconPath(icon),
          radioButton: !showUpgradeBadge,
          radioName: 'layoutOption',
          selected: this.currentCardsLayout,
          separator: index === this.cardsLayout.length - 1,
          inputLabel: this.getInputLabelForLayoutItem(id),
          ...(showUpgradeBadge && {
            badge: {
              icon: 'lock',
              text: 'upgradePlan',
            },
            disabled: true,
          }),
        };
      });

      const resetLayout = [
        {
          id: 'resetLayout',
          label: this.resetLayoutLabel,
          customIconName: this.iconPath('undo'),
        },
      ];

      if (this.isCardsLayoutCalendar) {
        const showWeekends = [
          {
            id: 'showWeekends',
            value: this.showWeekends,
            label: this.translate('showWeekends'),
            separator: true,
            toggleButton: true,
          },
        ];
        if (this.isSprint) {
          return [...showWeekends];
        }
        return [...layouts, ...showWeekends, ...resetLayout];
      }

      const sortOptions = this.filteredBoardSortByOptions.map(
        ({ value: id, label, icon }, index) => {
          return {
            id,
            label: this.translate(label),
            customIconName: this.iconPath(icon),
            radioButton: true,
            radioName: 'sortOption',
            selected: this.sortBySelectedOption,
            groupLabel: index === 0 ? this.translate('sortBy') : '',
            separator: index === this.filteredBoardSortByOptions.length - 1,
            disabled:
              (id === 'status' && !this.isViewOrMyWork) ||
              (id === 'estimate' &&
                !this.$store.getters.getEstimateSystemForProject(this.currentBoard.project_id) &&
                !this.isViewOrMyWork),
          };
        }
      );

      const groupByOptions = this.filteredGroupByOptions.map(
        ({ value: id, label, icon }, index) => {
          return {
            id,
            label: this.translate(label),
            customIconName: this.iconPath(icon),
            radioButton: true,
            radioName: 'groupByOption',
            selected: this.currentBoardGroupBy,
            groupLabel: index === 0 ? this.translate('groupBy') : '',
          };
        }
      );
      const hideEmptyGroups = [
        {
          id: 'hideEmptyGroups',
          value: this.hideEmptyGroups,
          label: this.translate('hideEmptyGroups'),
          separator: true,
          toggleButton: true,
        },
      ];

      let options = [];

      if (this.isSprint) {
        options = [...groupByOptions, ...hideEmptyGroups, ...sortOptions, ...resetLayout];
      } else if (this.isViewOrMyWork) {
        options = [...sortOptions, ...resetLayout];
      } else {
        options = [
          ...layouts,
          ...groupByOptions,
          ...hideEmptyGroups,
          ...sortOptions,
          ...resetLayout,
        ];
      }

      return options;
    },
    selectedDropdownItem() {
      return this.dropdownItems.find((item) => item.id === this.currentCardsLayout);
    },
    isCardsLayoutCalendar() {
      return this.currentBoardId && this.currentCardsLayout === BoardLayout.Calendar;
    },
    currentCardsLayout: {
      get() {
        if (this.embed && !this.inEpicBoard)
          return (
            this.embeddedBoardAttributes?.layout ||
            this.$store.getters.cardsLayout(this.currentBoardId)
          );

        return this.$store.getters.cardsLayout(this.currentBoardId);
      },
      set(cardsLayout) {
        if (cardsLayout === this.currentCardsLayout) return;
        this.$store.dispatch('setCardsLayout', {
          value: cardsLayout,
          boardId: this.currentBoardId,
        });
        if (this.inEpicBoard || this.$route.params?.cardsLayout === cardsLayout) return;

        const routeName =
          this.currentBoard.id === this.$constants.epicsBoardId
            ? this.$constants.epicsBoardRoute.name
            : this.$constants.routeNames.board;

        this.$router.push({ name: routeName, params: { ...this.$route.params, cardsLayout } });
      },
    },

    currentBoardSortBy: {
      get() {
        return this.embed && !this.inEpicBoard
          ? this.embeddedBoardAttributes?.sortBy || 'manual'
          : this.$store.getters.getBoardSortBy(this.currentBoardId);
      },
      set(sortBy) {
        if (sortBy === this.currentBoardSortBy) return;
        if (this.embed && !this.inEpicBoard) {
          this.$emit('updateAttribute', { 'sort-by': sortBy });
          return;
        }
        this.$store.dispatch('setBoardSortBy', { value: sortBy, boardId: this.currentBoardId });
      },
    },

    myWorkUserId() {
      return this.$store.getters.getMyWorkUserId;
    },

    myWorkSortBy() {
      return this.$store.getters.getMyWorkSortBy[this.myWorkUserId]?.field || 'time_created';
    },

    currentViewSortBy: {
      get() {
        if (this.isMyWork) {
          return this.myWorkSortBy;
        }
        return this.$store.getters.getViewFilters?.sort_by?.field || 'time_created';
      },
      set(sortBy) {
        if (sortBy === this.currentViewSortBy) return;
        this.$store.dispatch('setViewEditMode', this.currentViewSortBy !== sortBy);
        const filters = { ...this.$store.getters.getViewFilters };
        filters['sort_by'] = {
          field: sortBy,
          order_ascending: sortBy === 'due_date' || sortBy === 'status' ? true : false,
        };
        if (this.isMyWork) {
          this.$store.dispatch('setMyWorkSortBy', {
            userId: this.myWorkUserId,
            value: filters['sort_by'],
          });
        }
        this.setViewFilters(filters);
      },
    },

    currentBoardGroupBy: {
      get() {
        if (this.embed && !this.inEpicBoard) {
          return this.embeddedBoardAttributes?.groupBy || groupByOption.status;
        }
        return this.$store.getters.getBoardGroupBy(this.currentBoardId);
      },
      set(groupBy) {
        if (groupBy === this.currentBoardGroupBy) return;

        if (this.embed && !this.inEpicBoard) {
          this.$emit('updateAttribute', { 'group-by': groupBy });
          return;
        }
        this.$store.dispatch('setBoardGroupBy', {
          value: groupBy,
          boardId: this.currentBoardId,
        });
      },
    },

    showBlueDot() {
      return (
        this.currentBoardSortBy !== this.defaultSortByOpt ||
        this.currentBoardGroupBy !== this.defaultGroupByOpt ||
        this.hideEmptyGroups ||
        (this.isViewOrMyWork && this.currentViewSortBy !== 'time_created') ||
        (this.currentCardsLayout !== this.currentBoard?.layout &&
          ((this.isBoard && this.notNewBoard) || (this.embed && !this.inEpicBoard))) ||
        // layout prop is missing in epics board
        // and there is no option to permanently set the layout for epics board
        ((this.isEpicsBoard || this.inEpicBoard) && this.currentCardsLayout !== BoardLayout.Board)
      );
    },

    isMyWork() {
      return (
        [this.$constants.myWorkRouteName, this.$constants.memberRouteName].includes(
          this.$route.name
        ) && !this.inEpicBoard
      );
    },

    isViewOrMyWork() {
      return (this.isViewOpen || this.isMyWork) && !this.inEpicBoard;
    },

    layoutDropdownIcon() {
      if (this.isSprint) {
        const icon =
          this.$store.getters.getSprintSettings(this.currentBoard.project_id)?.layout ||
          BoardLayout.Board;
        return this.iconPath(icon);
      }
      return this.selectedDropdownItem?.customIconName ?? this.iconPath('list');
    },

    sortBySelectedOption() {
      return this.isViewOrMyWork ? this.currentViewSortBy : this.currentBoardSortBy;
    },

    hideEmptyGroups() {
      return this.embed && !this.inEpicBoard
        ? this.embeddedBoardAttributes.hideEmptyGroups
        : this.$store.getters.getHideEmptyGroups(this.currentBoardId);
    },

    showWeekends() {
      return this.$store.getters.getShowWeekends;
    },

    resetLayoutLabel() {
      return this.isViewOrMyWork
        ? this.translate('resetLayoutToViewDefault')
        : this.translate('resetLayoutToBoardDefault');
    },

    isBoard() {
      return this.$route.name === 'board' && !this.inEpicBoard;
    },

    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint && !this.inEpicBoard;
    },

    isEpicsBoard() {
      return this.$route.name === this.$constants.epicsBoardRoute.name && !this.inEpicBoard;
    },

    notNewBoard() {
      return this.$route.name !== 'newboard' && !this.inEpicBoard;
    },
  },

  beforeMount() {
    this.checkForLayoutRoute();
  },

  methods: {
    onDropdownItemClick(item, hideFn) {
      if (typeof hideFn === 'function') {
        hideFn();
      }
      if (item.id) {
        if (item.id === 'hideEmptyGroups') {
          if (this.embed && !this.inEpicBoard) {
            this.$emit('updateAttribute', { 'hide-empty-groups': !item.value });
            return;
          }
          this.$store.dispatch('setHideEmptyGroups', {
            value: !item.value,
            boardId: this.currentBoardId,
          });
          return;
        }
        if (item.id === 'resetLayout') {
          this.resetLayout();
          return;
        }
        if (item.id === 'showWeekends') {
          this.$store.dispatch('setShowWeekends', !item.value);
          return;
        }
        switch (item.radioName) {
          case 'layoutOption':
            this.handleLayoutOptionClick(item);
            return;
          case 'sortOption':
            this.setSortOption(item.id);
            return;
          case 'groupByOption':
            this.setGroupByOption(item.id);
            return;
          default:
            return;
        }
      }
    },

    resetLayout() {
      const defaultLayout = this.currentBoard?.layout || BoardLayout.Board;
      if (this.embed && !this.inEpicBoard) {
        this.$emit('updateAttribute', { layout: defaultLayout });
        this.$emit('updateAttribute', { 'group-by': groupByOption.status });
        this.$emit('updateAttribute', { 'sort-by': 'manual' });
        return;
      }

      this.$store.dispatch('setCardsLayout', {
        boardId: this.currentBoardId,
        value: defaultLayout,
      });
      this.$store.dispatch('setBoardSortBy', {
        boardId: this.currentBoardId,
        value: this.defaultSortByOpt,
      });
      this.$store.dispatch('setBoardGroupBy', {
        boardId: this.currentBoardId,
        value: this.defaultGroupByOpt,
      });
      this.$store.dispatch('setHideEmptyGroups', {
        value: false,
        boardId: this.currentBoardId,
      });
      this.$store.dispatch('setShowWeekends', false);
      if (this.$route.params?.cardsLayout !== defaultLayout && !this.inEpicBoard) {
        this.$router.replace({
          params: { ...this.$route.params, cardsLayout: defaultLayout },
        });
      }
      browserStorageGetAndSetItem(
        localBoardLayoutKey(this.$store.getters.getTeamID),
        this.currentBoardId,
        defaultLayout
      );
      if (this.isViewOrMyWork) {
        const field = 'time_created';
        this.setSortOption(field);
      }
    },

    setLayoutOption(id) {
      this.currentCardsLayout = id;
      if (
        this.currentBoardGroupBy === groupByOption.none &&
        this.currentCardsLayout === BoardLayout.Board
      ) {
        this.currentBoardGroupBy = groupByOption.status;
      }
    },

    setSortOption(id) {
      if (this.isViewOrMyWork) {
        if (id === this.currentViewSortBy) return;
        this.$store.dispatch('setViewEditMode', this.currentViewSortBy !== id);
        this.$nextTick(() => {
          this.currentViewSortBy = id;
        });
      } else {
        this.currentBoardSortBy = id;
      }
    },

    setGroupByOption(id) {
      this.currentBoardGroupBy = id;
    },

    getInputLabelForLayoutItem(id) {
      let inputLabel = '';
      if (this.currentBoard?.layout) {
        if (this.currentBoard.layout === id) {
          inputLabel = this.translate('default');
        }
      } else {
        const { value } = this.cardsLayout.find((option) => option.default);
        if (value === id) {
          inputLabel = this.translate('default');
        }
      }
      return inputLabel;
    },

    checkForLayoutRoute() {
      if (this.$route.params?.cardsLayout && this.$store.getters.isPreviousRouteRoot) {
        this.currentCardsLayout = this.$route.params.cardsLayout;
      }
    },

    handleLayoutOptionClick(item) {
      if (
        !this.allowSwitchingToTimelineLayout(item.id) ||
        !this.allowSwitchingToCalendarLayout(item.id)
      )
        return;

      if (this.embed && !this.inEpicBoard) {
        this.$emit('updateAttribute', { layout: item.id });
        return;
      }
      this.currentCardsLayout = item.id;
      browserStorageGetAndSetItem(
        localBoardLayoutKey(this.$store.getters.getTeamID),
        this.currentBoardId,
        item.id
      );
      if (
        this.currentBoardGroupBy === groupByOption.none &&
        this.currentCardsLayout === BoardLayout.Board
      ) {
        this.currentBoardGroupBy = groupByOption.status;
      }
      this.setLayoutOption(item.id);
    },
  },
};
</script>
